import { makeAutoObservable } from "mobx";

import { getNextQuestionId } from "components/AbnSoleTraderRegisterForm/components/DecisionStep/decisionFlowConfig";
import { type CompanyClient } from "services/company/companyClient";
import {
  AustralianState,
  CompanyAbnApplicationReasonUpdateData,
  CompanyAbnBusinessDescriptionUpdateData,
  CompanyAbnCreateData,
  CompanyAbnExistingAbnUpdateData,
  CompanyAbnFirstTimeBusinessOrSelfEmployedUpdateData,
  CompanyAbnFirstTimeDirectorUpdateData,
  CompanyAbnFirstTimeSelfEmployedOrContractorUpdateData,
  CompanyAbnSoleTraderDataUpdateData,
  CompanyAcnNumberUpdateData,
  CompanyAgriculturalPropertyOperationsUpdateData,
  CompanyAsicAgentNominationUpdateData,
  type CompanyCountry,
  CompanyCountryUpdateData,
  CompanyCreateData,
  type CompanyData,
  CompanyMainBusinessActivityUpdateData,
  CompanyNamePostfix,
  CompanyNameUpdateData,
  CompanyPoaUpdateData,
  CompanyPricingPlanUpdateData,
  CompanySharesUpdateData,
  CompanySoleTraderDecisionUpdateData,
  CompanyStateUpdateData,
  type CompanyUpdateData,
  CompanyUseVirtualOfficeUpdateData,
  type SoleTraderDecisionAnswerEntry,
} from "services/company/companyTypes";
import type { FeatureStore } from "services/feature/featureStore";
import { type PricingPlan } from "services/pricingPlan/pricingPlanTypes";

interface CompanyState {
  company?: CompanyData;
}

export class CompanyStore {
  private _state: CompanyState = {};

  constructor(
    private readonly _companyClient: CompanyClient,
    private readonly _featureStore: FeatureStore,
  ) {
    makeAutoObservable(this);
  }

  get state(): CompanyState {
    return this._state;
  }

  set state(value: CompanyState) {
    this._state = value;
  }

  get company(): CompanyData | undefined {
    return this.state.company;
  }

  private set company(company) {
    this.state.company = company;
  }

  async init(): Promise<void> {
    this.company = await this._companyClient.getCompany();
  }

  getCompany(): CompanyData {
    if (!this.company) {
      throw new Error("Company not found");
    }

    return this.company;
  }

  skipVOBillingPeriod(): boolean {
    if (this.getCompany().useHomeAddressAsRegistered) {
      return true;
    }

    return !this.getCompany().useVirtualOffice;
  }

  skipVOAddressSelection(): boolean {
    if (this.getCompany().useHomeAddressAsRegistered) {
      return true;
    }
    return !!this.getCompany().useVirtualOffice;
  }

  checkAcnCompanyDetailExists(): boolean {
    const company = this.getCompany();
    // For AU: Check if pricing plan and virtual office selection are done
    // Address is checked separately in companyAddressStore
    return !!(company.companyName && company.pricingPlan && company.useVirtualOffice !== undefined);
  }

  checkAbnCompanyDetailExists(): boolean {
    const company = this.getCompany();
    return !!(company.stateOfRegistration && company.acn && company.natureOfBusiness);
  }

  async updatePoa(poaFileId: string): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyPoaUpdateData({ poaFileId }),
    );
  }

  async updateShares(totalShares: string, totalCapital: string): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanySharesUpdateData({
        totalSharesNum: parseInt(totalShares),
        totalCapitalGbp: parseInt(totalCapital),
      }),
    );
  }

  async updateName(name: string, namePostfix?: CompanyNamePostfix, letterNoObjectionFileId?: string): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyNameUpdateData({ companyName: name, namePostfix, letterNoObjectionFileId }),
    );
  }

  async updateNameWithAbn(name: string, namePostfix?: CompanyNamePostfix, nameIdenticalAbn?: string): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyAbnCreateData({ companyName: name, namePostfix, nameIdenticalAbn }),
    );
  }

  async updateCountry(country?: CompanyCountry): Promise<void> {
    if (!country) {
      return;
    }

    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyCountryUpdateData({ country }),
    );
  }

  async updatePricingPlan(pricingPlan: PricingPlan | null): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyPricingPlanUpdateData({ pricingPlan }),
    );
  }

  async updateVirtualOffice(useVirtualOffice: boolean, useHomeAddressAsRegistered: boolean): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyUseVirtualOfficeUpdateData({ useVirtualOffice, useHomeAddressAsRegistered }),
    );
  }

  async updateAbnBusinessDescription(natureOfBusiness: string): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyAbnBusinessDescriptionUpdateData({ natureOfBusiness }),
    );
  }

  async updateAgriculturalPropertyOperations(isConductAgriculturalPropertyOperations: boolean): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyAgriculturalPropertyOperationsUpdateData({ isConductAgriculturalPropertyOperations }),
    );
  }

  async updateAsicAgentNomination(auAsicAgentNominationRequested: boolean, asicAgentSigFileId?: string): Promise<void> {
    await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyAsicAgentNominationUpdateData({ auAsicAgentNominationRequested, asicAgentSigFileId }),
    );
    // The update response does not echo the ASIC nomination fields, so re-fetch
    // the company to keep the store (and the form's initial values) in sync with
    // what was persisted.
    await this.init();
  }

  async updateCompanyStateOfRegistration(stateOfRegistration: AustralianState): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyStateUpdateData({ stateOfRegistration }),
    );
  }

  async updateMainBusinessActivity(
    businessActivityCode: string,
    businessActivityDescription: string,
    businessActivityAddressId: string,
  ): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyMainBusinessActivityUpdateData({
        businessActivityCode,
        businessActivityDescription,
        businessActivityAddressId,
      }),
    );
  }

  async updateAcnNumber(acn: string): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyAcnNumberUpdateData({ acn }),
    );
  }

  async updateCompany(data: CompanyUpdateData): Promise<void> {
    this.company = await this._companyClient.updateCompany(this.getCompany().id, data);
  }

  async createCompany(): Promise<void>;
  async createCompany(companyName: string, namePostfix: CompanyNamePostfix): Promise<void>;
  async createCompany(companyName: string, namePostfix: CompanyNamePostfix, nameIdenticalAbn: string): Promise<void>;
  async createCompany(
    companyName?: string,
    namePostfix?: CompanyNamePostfix,
    nameIdenticalAbn?: string,
  ): Promise<void> {
    if (companyName && namePostfix && nameIdenticalAbn) {
      this.company = await this._companyClient.createCompany(
        new CompanyAbnCreateData({ companyName, namePostfix, nameIdenticalAbn }),
      );
    } else if (companyName && namePostfix) {
      this.company = await this._companyClient.createCompany(new CompanyCreateData({ companyName, namePostfix }));
    } else {
      this.company = await this._companyClient.createCompany();
    }

    // on company creation backend can set some features to customer, so let's update them
    await this._featureStore.reload();
  }

  async updateSoleTraderDecision(soleTraderDecision: SoleTraderDecisionAnswerEntry[]): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanySoleTraderDecisionUpdateData({ soleTraderDecision }),
    );
  }

  /**
   * Checks if the Sole Trader decision flow is complete by examining the last entry
   * in the soleTraderDecision array and checking if its nextQuestionId is "0".
   *
   * The decision flow is considered complete when the last answered question's
   * nextQuestionId points to "0" according to the decision flow configuration.
   *
   * @returns true if the decision flow is complete, false otherwise
   */
  isSoleTraderDecisionComplete(): boolean {
    const company = this.company;

    // If no company or no decision data, flow is not complete
    if (!company?.soleTraderDecision || company.soleTraderDecision.length === 0) {
      return false;
    }

    // Get the last entry from the decision trail
    const lastEntry = company.soleTraderDecision[company.soleTraderDecision.length - 1];

    // Safety check for lastEntry (should always exist due to length check above)
    if (!lastEntry) {
      return false;
    }

    // Check if the next question ID for this answer is "0" (flow complete)
    const nextQuestionId = getNextQuestionId(lastEntry.questionId, lastEntry.answerId);
    return nextQuestionId === "0";
  }

  async updateAbnSoleTraderData(abnSoleTraderData: string): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyAbnSoleTraderDataUpdateData({ abnSoleTraderData }),
    );
  }

  async updateAbnApplicationReason(abnApplicationReason: string): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyAbnApplicationReasonUpdateData({ abnApplicationReason }),
    );
  }

  async updateAbnExistingAbn(abnExistingAbn: string): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyAbnExistingAbnUpdateData({ abnExistingAbn }),
    );
  }

  async updateAbnFirstTimeBusinessOrSelfEmployed(abnFirstTimeBusinessOrSelfEmployed: boolean): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyAbnFirstTimeBusinessOrSelfEmployedUpdateData({ abnFirstTimeBusinessOrSelfEmployed }),
    );
  }

  async updateAbnFirstTimeSelfEmployedOrContractor(abnFirstTimeSelfEmployedOrContractor: boolean): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyAbnFirstTimeSelfEmployedOrContractorUpdateData({ abnFirstTimeSelfEmployedOrContractor }),
    );
  }

  async updateAbnFirstTimeDirector(abnFirstTimeDirector: boolean): Promise<void> {
    this.company = await this._companyClient.updateCompany(
      this.getCompany().id,
      new CompanyAbnFirstTimeDirectorUpdateData({ abnFirstTimeDirector }),
    );
  }
}
